export type AnnouncementIcon = "building" | "sparkles" | "message" | "chart" | "video";

/** Pages on the public site, as paths under its language root (`product/centers`). */
export interface AnnouncementLinkPaths {
    moreInfo?: string;
    documentation?: string;
}

/** The same pages as full URLs in the therapist's site language. */
export interface AnnouncementLinks {
    moreInfo?: string;
    documentation?: string;
}

/**
 * A picture shipped with the app, under `public/images/announcements/`. Its alt
 * text lives with the rest of the copy, at `announcements.items.<id>.imageAlt`.
 */
export interface AnnouncementImage {
    src: string;
}

/**
 * One piece of platform news for therapists.
 *
 * What it says lives in the locales under `announcements.items.<id>`, so it
 * gets both languages and the therapist's own vocabulary like any other copy.
 */
export interface Announcement {
    /**
     * Stable forever: the server remembers who acknowledged it by this id.
     * Lowercase words joined by hyphens — the only shape the API accepts.
     */
    id: string;
    /** ISO date from which it is shown. */
    publishedAt: string;
    /** ISO date from which it is no longer shown, to anyone. */
    expiresAt?: string;
    icon: AnnouncementIcon;
    /** Keys under `announcements.items.<id>.highlights`, in display order. */
    highlights: readonly string[];
    links?: AnnouncementLinkPaths;
    /** Shown in place of the icon: news about something you can see is best shown. */
    image?: AnnouncementImage;
}

export interface AnnouncementCopyKeys {
    title: string;
    body: string;
    highlights: string[];
    /** Present only when the announcement has a picture. */
    imageAlt?: string;
}

export const announcementCopyKeys = (announcement: Announcement): AnnouncementCopyKeys => {
    const root = `announcements.items.${announcement.id}`;
    return {
        title: `${root}.title`,
        body: `${root}.body`,
        highlights: announcement.highlights.map((highlight) => `${root}.highlights.${highlight}`),
        ...(announcement.image ? { imageAlt: `${root}.imageAlt` } : {}),
    };
};

const PUBLIC_SITE = "https://alchely.com";

/** The public site speaks the two languages the app does, and nothing else. */
const siteLanguage = (language: string | undefined): "es" | "en" =>
    language?.startsWith("es") ? "es" : "en";

/**
 * The announcement's pages on the public site, in the therapist's language.
 * Paths are kept language-free in the catalogue so one entry serves both.
 */
export const announcementLinks = (
    announcement: Announcement,
    language: string | undefined,
): AnnouncementLinks => {
    const lang = siteLanguage(language);
    const toUrl = (path: string) => `${PUBLIC_SITE}/${lang}/${path}/`;
    const { moreInfo, documentation } = announcement.links ?? {};

    const links: AnnouncementLinks = {};
    if (moreInfo) links.moreInfo = toUrl(moreInfo);
    if (documentation) links.documentation = toUrl(documentation);
    return links;
};

/** What the therapist still has to see: published, unexpired, unacknowledged, newest first. */
export const selectPendingAnnouncements = (
    catalog: readonly Announcement[],
    acknowledgedIds: readonly string[],
    now: Date,
): Announcement[] => {
    const acknowledged = new Set(acknowledgedIds);
    const nowTime = now.getTime();

    return catalog
        .filter((announcement) => !acknowledged.has(announcement.id))
        .filter((announcement) => Date.parse(announcement.publishedAt) <= nowTime)
        .filter(
            (announcement) =>
                announcement.expiresAt === undefined || Date.parse(announcement.expiresAt) > nowTime,
        )
        .sort((a, b) => Date.parse(b.publishedAt) - Date.parse(a.publishedAt));
};
